import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import { Escalation } from "@/types";

export function useEscalations(
  projectId: string,
  statusFilter?: "open" | "resolved",
) {
  const client = useApiClient();

  return useQuery({
    queryKey: ["escalations", projectId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      const response = await client.get<{ escalations: Escalation[] }>(
        `/api/projects/${projectId}/escalations?${params.toString()}`,
      );
      return response.data.escalations;
    },
    enabled: !!projectId,
  });
}


// ─── Task #9: create an ad-hoc project meeting (Tier A — no Microsoft Graph) ─────────────────

/** Body for POST /api/projects/{projectId}/meetings (specs/api/meetings.yaml). */
export interface CreateProjectMeetingInput {
  meeting_name: string;
  phase: "Phase 0" | "Phase 1" | "Phase 2" | "Phase 3" | "Phase 4";
  scope: "internal" | "customer";
  /** ISO-8601 date-time (with offset) or YYYY-MM-DD. */
  meeting_date?: string;
  /**
   * T-17 — URL of a meeting held outside DeliverPro (https only). The server classifies it: an
   * avoma.com URL becomes `avoma_link` (+ transcript ingest), anything else `external_link_url`.
   */
  link?: string;
}

/** The created row — the subset the UI needs (full shape in specs/api/meetings.yaml). */
export interface CreatedProjectMeeting {
  id: string;
  project_id: string;
  phase: string;
  meeting_name: string;
  /** Canonical Avoma title built server-side — never empty. Used verbatim as the Teams subject. */
  name_template: string;
  scope: "internal" | "customer";
  status: string;
  meeting_date: string | null;
  avoma_link?: string | null;
  external_link_url?: string | null;
}

/**
 * Create a meeting mapped to the project. The server builds the canonical `name_template`, so the
 * Avoma sync can match the recording into the meeting-intelligence workflow. Invalidates the
 * project's meeting list (`['project-meetings', projectId]`, owned by MeetingTimeline).
 */
export function useCreateProjectMeeting(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<CreatedProjectMeeting, unknown, CreateProjectMeetingInput>(
    {
      mutationFn: async (input) => {
        const response = await client.post<CreatedProjectMeeting>(
          `/api/projects/${projectId}/meetings`,
          input,
        );
        return response.data;
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ["project-meetings", projectId],
        });
      },
    },
  );
}
